import React from 'react';
import { Box, Button, Chip, Grid, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { clearFinancialSupportWizard, readFinancialSupportPrograms, readFinancialSupportWizard, writeFinancialSupportPrograms } from './financialSupportProgramWizardStorage';
import {
  FinancialSupportReadOnlyDetail,
  FinancialSupportWizardSidebar,
  wizardPageSubtitleSx,
  wizardPageTitleSx,
  wizardPrimaryButtonSx,
  wizardSecondaryButtonSx,
  wizardSectionCardSx,
  wizardStickyFooterSx,
  wizardTextActionButtonSx,
  wizardWidePageShellSx,
} from '../../components/financialSupportWizard';

const stepItems = [
  { id: 1, label: 'Basic Info', completed: true },
  { id: 2, label: 'Contribution Rules', completed: true },
  { id: 3, label: 'Plans', completed: true },
  { id: 4, label: 'Review & Publish', active: true },
];

function formatValidityRange(start, end) {
  if (!start || !end) {
    return '-';
  }

  return `${dayjs(start).format('DD/MM/YYYY')} - ${dayjs(end).format('DD/MM/YYYY')}`;
}

export default function CreateFinancialSupportProgramReview() {
  const navigate = useNavigate();
  const wizardData = readFinancialSupportWizard();
  const basicInfo = wizardData.basicInfo || {};
  const contributionRules = wizardData.contributionRules || {};
  const plans = Array.isArray(wizardData.plans) ? wizardData.plans : [];
  const editingProgramId = wizardData.editingProgramId;

  const publishProgram = () => {
    const programs = readFinancialSupportPrograms();
    const publishedProgram = {
      id: editingProgramId || Date.now(),
      programName: basicInfo.programName,
      type: basicInfo.supportType,
      provider: basicInfo.serviceProvider,
      region: basicInfo.region,
      status: 'ACTIVE',
      validity: formatValidityRange(basicInfo.validityStart, basicInfo.validityEnd),
      lastUpdated: dayjs().format('M/D/YYYY'),
      basicInfo,
      contributionRules,
      plans,
    };
    const nextPrograms = editingProgramId
      ? [publishedProgram, ...programs.filter((program) => String(program.id) !== String(editingProgramId))]
      : [publishedProgram, ...programs];
    writeFinancialSupportPrograms(nextPrograms);
    clearFinancialSupportWizard();
    navigate('/financialSupport');
  };

  return (
    <Box sx={wizardWidePageShellSx}>
      <Typography className="app-page-title" sx={{ ...wizardPageTitleSx, mb: 1 }}>
        Review & Publish
      </Typography>
      <Typography sx={{ ...wizardPageSubtitleSx, mb: 4.5 }}>
        Review the program configuration below before publishing it. This summary uses the data collected across the previous steps.
      </Typography>

      <Grid container spacing={3} alignItems="start">
        <Grid size={{ xs: 12, md: 3.2, lg: 3 }}>
          <Box sx={{ position: { md: 'sticky' }, top: 88 }}>
            <FinancialSupportWizardSidebar steps={stepItems} />
          </Box>
        </Grid>

        <Grid size={{ xs: 12, md: 8.8, lg: 9 }}>
          <Stack spacing={3}>
            <Box sx={wizardSectionCardSx}>
              <Typography sx={{ fontSize: 20, lineHeight: '28px', fontWeight: 700, color: '#1C1B1F', mb: 3 }}>
                Basic Info
              </Typography>
              <Grid container spacing={2.5}>
                <Grid size={{ xs: 12, sm: 6 }}><FinancialSupportReadOnlyDetail label="Program Name" value={basicInfo.programName} /></Grid>
                <Grid size={{ xs: 12, sm: 6 }}><FinancialSupportReadOnlyDetail label="Support Type" value={basicInfo.supportType} /></Grid>
                <Grid size={{ xs: 12, sm: 6 }}><FinancialSupportReadOnlyDetail label="Service Provider" value={basicInfo.serviceProvider} /></Grid>
                <Grid size={{ xs: 12, sm: 6 }}><FinancialSupportReadOnlyDetail label="Region" value={basicInfo.region} /></Grid>
                <Grid size={{ xs: 12, sm: 6 }}><FinancialSupportReadOnlyDetail label="Currency" value={basicInfo.currency} /></Grid>
                <Grid size={{ xs: 12, sm: 6 }}><FinancialSupportReadOnlyDetail label="Validity Range" value={formatValidityRange(basicInfo.validityStart, basicInfo.validityEnd)} /></Grid>
                <Grid size={12}><FinancialSupportReadOnlyDetail label="Description" value={basicInfo.description} /></Grid>
              </Grid>
            </Box>

            <Box sx={wizardSectionCardSx}>
              <Typography sx={{ fontSize: 20, lineHeight: '28px', fontWeight: 700, color: '#1C1B1F', mb: 3 }}>
                Contribution Rules
              </Typography>
              <Grid container spacing={2.5}>
                <Grid size={{ xs: 12, sm: 6 }}><FinancialSupportReadOnlyDetail label="Setup Fee Contribution Required" value={contributionRules.setupFeeContributionRequired ? 'Yes' : 'No'} /></Grid>
                <Grid size={{ xs: 12, sm: 6 }}><FinancialSupportReadOnlyDetail label="Setup Fee Percentage" value={`${contributionRules.setupFeePercentage ?? 0}%`} /></Grid>
                <Grid size={{ xs: 12, sm: 6 }}><FinancialSupportReadOnlyDetail label="Subscription Fee Contribution Required" value={contributionRules.subscriptionFeeContributionRequired ? 'Yes' : 'No'} /></Grid>
                <Grid size={{ xs: 12, sm: 6 }}><FinancialSupportReadOnlyDetail label="Subscription Fee Percentage" value={`${contributionRules.subscriptionFeePercentage ?? 0}%`} /></Grid>
              </Grid>
            </Box>

            <Box sx={wizardSectionCardSx}>
              <Typography sx={{ fontSize: 20, lineHeight: '28px', fontWeight: 700, color: '#1C1B1F', mb: 3 }}>
                Configured Plans
              </Typography>
              <Stack spacing={2}>
                {plans.length ? plans.map((plan, index) => (
                  <Box key={plan.id || index} sx={{ border: '1px solid #DFE5F6', borderRadius: '16px', p: 2.5 }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
                      <Typography sx={{ fontSize: 16, lineHeight: '24px', fontWeight: 700, color: '#1C1B1F' }}>
                        Plan {index + 1}: {plan.planName}
                      </Typography>
                      <Chip label={plan.supportType} size="small" sx={{ bgcolor: '#EEF3FF', color: '#294190', fontWeight: 600 }} />
                      <Chip label={plan.planStatus} size="small" sx={{ bgcolor: '#EEF8DE', color: '#4F8506', fontWeight: 600 }} />
                    </Stack>
                    <Grid container spacing={2.5}>
                      <Grid size={{ xs: 12, sm: 6, lg: 4 }}><FinancialSupportReadOnlyDetail label="Amount" value={`${plan.currency} ${plan.planAmount ?? '-'}`} /></Grid>
                      <Grid size={{ xs: 12, sm: 6, lg: 4 }}><FinancialSupportReadOnlyDetail label="Interest Rate" value={plan.interestRateApplicable ? `${plan.interestRate}%` : 'Not applicable'} /></Grid>
                      <Grid size={{ xs: 12, sm: 6, lg: 4 }}><FinancialSupportReadOnlyDetail label="Repayment Duration" value={plan.repaymentDurationApplicable ? `${plan.repaymentDuration} months` : 'Not applicable'} /></Grid>
                      <Grid size={12}><FinancialSupportReadOnlyDetail label="Description" value={plan.description} /></Grid>
                    </Grid>
                  </Box>
                )) : (
                  <Typography sx={{ fontSize: 15, lineHeight: '22px', color: '#6B6880' }}>
                    No plans have been added yet.
                  </Typography>
                )}
              </Stack>
            </Box>

            <Box sx={wizardStickyFooterSx}>
              <Box sx={{ display: 'flex', gap: 1.5, flexDirection: { xs: 'column', sm: 'row' }, width: { xs: '100%', md: 'auto' } }}>
                <Button onClick={() => navigate('/financialSupport')} sx={wizardTextActionButtonSx}>
                  Cancel
                </Button>
                <Button onClick={() => navigate('/financialSupport/create-program/plans')} sx={wizardSecondaryButtonSx}>
                  Previous
                </Button>
              </Box>
              <Button
                variant="contained"
                onClick={publishProgram}
                sx={{ ...wizardPrimaryButtonSx, minWidth: { sm: 160 } }}
              >
                Publish
              </Button>
            </Box>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
}
